import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Pagination,
  CircularProgress,
  Tooltip,
  Chip
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DownloadIcon from '@mui/icons-material/Download';
import Slider from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import PageWrapper from './PageWrapper';

const AudioProgress = styled(Slider)(({ theme }) => ({
  height: 3,
  padding: 0,
  '& .MuiSlider-thumb': {
    width: 8,
    height: 8,
    opacity: 0,
    transition: 'opacity 0.2s',
    '&:hover': {
      boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.16)}`
    }
  },
  '& .MuiSlider-rail': {
    opacity: 0.15,
    backgroundColor: theme.palette.primary.main,
    height: 3,
  },
  '& .MuiSlider-track': {
    transition: 'width 0.1s linear',
    backgroundColor: theme.palette.primary.main,
    border: 'none',
    height: 3,
  },
  '&:hover .MuiSlider-thumb': {
    opacity: 1
  }
}));

const CallRow = ({ call }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audio, setAudio] = useState(null);
  const [progress, setProgress] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (audio) {
      // Set up event listeners when audio is available
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleAudioEnd);
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });
      
      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleAudioEnd);
        audio.pause();
        audio.src = '';
        if (audioUrl) {
          URL.revokeObjectURL(audioUrl);
        }
      };
    }
  }, [audio, audioUrl]);

  const handleTimeUpdate = (e) => {
    const currentAudio = e.target;
    if (currentAudio && currentAudio.duration) {
      const percentage = (currentAudio.currentTime / currentAudio.duration) * 100;
      setProgress(percentage);
    }
  };

  const handleAudioEnd = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  const getRecordingEndpoint = (call) => {
    if (!call) return null;
    
    // Use recording_url if available
    if (call.recording_url) {
      return call.recording_url;
    }
    
    // Use s3_recording_key if available
    if (call.s3_recording_key) {
      return `/api/recordings/s3/${call.s3_recording_key}`;
    }
    
    // Use Twilio SID as fallback
    if (call.sid) {
      return `/api/calls/twilio/${call.sid}/recording`;
    }
    
    return null;
  };

  // Determine if this call has a recording
  const hasRecording = !!call.s3_recording_key || !!call.recording_url || !!call.sid;

  const fetchAudio = async (call) => {
    try {
      const endpointUrl = getRecordingEndpoint(call);
      
      if (!endpointUrl) {
        console.error("No valid recording endpoint available for call:", call);
        setError("No recording available for this call");
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpointUrl}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (!response.ok) {
        console.error(`Failed to fetch recording: ${response.status} ${response.statusText}`);
        setError(`Failed to fetch recording: ${response.status} ${response.statusText}`);
        setLoading(false);
        return;
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      
      // Create new audio element
      const newAudio = new Audio(url);
      
      // Wait for audio to be loaded before playing
      newAudio.addEventListener('canplaythrough', () => {
        newAudio.play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch(error => {
            console.error('Error starting audio playback:', error);
            setError(`Error playing audio: ${error.message}`);
          });
      }, { once: true });
      
      // Set audio in state to trigger the useEffect for event listeners
      setAudio(newAudio);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching audio:", err);
      setError(`Error fetching audio: ${err.message}`);
      setLoading(false);
    }
  };

  const handleProgressChange = (_, newValue) => {
    if (audio && !isNaN(audio.duration)) {
      const time = (newValue / 100) * audio.duration;
      audio.currentTime = time;
      setProgress(newValue);
    }
  };

  const togglePlay = () => {
    if (loading) return;

    if (!audio) {
      setLoading(true);
      fetchAudio(call);
      return;
    }
    
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(error => {
          console.error('Error resuming audio playback:', error);
          setError(`Error playing audio: ${error.message}`);
        });
    }
  };

  const handleDownload = async () => {
    try {
      const recordingEndpoint = getRecordingEndpoint(call);
      
      if (!recordingEndpoint) {
        throw new Error('No recording available for this call');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${recordingEndpoint}`,
        { 
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch recording: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording_${call.sid || call.id}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Download error:", err);
      alert('Failed to download recording: ' + err.message);
    }
  };

  return (
    <TableRow>
      <TableCell>{new Date(call.date_created).toLocaleString()}</TableCell>
      <TableCell>{call.agent_name || 'Unknown Agent'}</TableCell>
      <TableCell>{call.from}</TableCell>
      <TableCell>{call.to}</TableCell>
      <TableCell>
        <Chip 
          label={call.type || call.direction || 'Unknown'} 
          size="small"
          color={
            call.type === 'Lead' || call.is_lead ? 'success' : 
            call.type === 'Enquiry' || call.is_enquiry ? 'info' : 
            'default'
          }
        />
      </TableCell>
      <TableCell>{Math.round(call.duration)} seconds</TableCell>
      <TableCell>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: isPlaying ? 140 : 80,
          position: 'relative',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 0
        }}>
          <IconButton 
            onClick={togglePlay}
            disabled={loading || !hasRecording}
            sx={{ 
              bgcolor: isPlaying ? 'primary.light' : 'action.hover',
              color: isPlaying ? 'common.white' : 'action.active',
              '&:hover': {
                bgcolor: isPlaying ? 'primary.main' : 'action.selected',
              },
              transition: 'all 0.2s',
              '&.Mui-disabled': {
                color: 'text.disabled',
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            {loading ? <CircularProgress size={16} /> : isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          {isPlaying && (
            <Slider
              sx={{ 
                mx: 1, 
                height: 4, 
                position: 'absolute',
                left: 40,
                right: 0,
                width: 100,
              }}
              value={progress}
              onChange={handleProgressChange}
              size="small"
            />
          )}
        </Box>
        <IconButton 
          onClick={handleDownload}
          disabled={loading || !hasRecording}
          sx={{ 
            ml: 1,
            '&.Mui-disabled': {
              color: 'text.disabled',
            },
          }}
        >
          <DownloadIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const CallRecordings = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCalls, setTotalCalls] = useState(0);
  const rowsPerPage = 20;

  useEffect(() => {
    fetchCalls(page);
  }, [page]);

  const fetchCalls = async (pageNumber) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/calls/twilio?page=${pageNumber}&pageSize=${rowsPerPage}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch calls: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Handle the updated response structure with pagination metadata
      if (data.calls) {
        setCalls(data.calls);
        setTotalPages(data.pages);
        setTotalCalls(data.total);
      } else {
        // Fallback for old API format that returns array directly
        setCalls(data);
      }
    } catch (error) {
      console.error('CallRecordings - Error fetching calls:', error);
      setError(`Failed to fetch calls: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <PageWrapper>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Call Recordings
        </Typography>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No recordings found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                calls.map((call) => (
                  <CallRow key={call.sid || call.id} call={call} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {totalCalls > 0 ? 
              `Showing ${(page - 1) * rowsPerPage + 1} - ${Math.min(page * rowsPerPage, totalCalls)} of ${totalCalls} recordings` : 
              'No recordings found'}
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      </Box>
    </PageWrapper>
  );
};

export default CallRecordings; 